import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { CheckoutService } from '../../services/checkout.service';
import { AuthService } from '../../services/auth.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ArkifiHubOrderHistoryDto, ArkifiHubOrderDto, OrderStatus } from '../../models/checkout.dto';

@Component({
  selector: 'app-orders',
  standalone: true,
  imports: [CommonModule, RouterModule, ToastModule, ProgressSpinnerModule],
  providers: [MessageService],
  templateUrl: './orders.html',
  styleUrl: './orders.css'
})
export class OrdersComponent implements OnInit {
  isLoading = signal(true);
  orders = signal<ArkifiHubOrderDto[]>([]);
  error = signal<string | null>(null);
  currentPage = signal(1);
  pageSize = signal(20);
  totalCount = signal(0);
  totalPages = signal(0);
  platformId = signal<string | null>(null);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private checkoutService: CheckoutService,
    private authService: AuthService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/signin']);
      return;
    }

    // Get platformId from route params if available
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.platformId.set(params['id']);
      }
      this.loadOrders();
    });
  }

  loadOrders(page: number = 1): void {
    this.isLoading.set(true);
    this.error.set(null);
    this.currentPage.set(page);

    this.checkoutService.getOrderHistory(
      this.platformId() || undefined,
      page,
      this.pageSize()
    ).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        if (response.success && response.data) {
          this.orders.set(response.data.orders);
          this.totalCount.set(response.data.totalCount);
          this.totalPages.set(response.data.totalPages);
          this.currentPage.set(response.data.pageNumber);
        } else {
          this.error.set(response.message || 'Failed to load orders');
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to load orders',
            life: 5000
          });
        }
      },
      error: (err) => {
        this.isLoading.set(false);
        const errorMessage = err.error?.message || 'An error occurred while loading orders';
        this.error.set(errorMessage);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
          life: 5000
        });
        console.error('Error loading orders:', err);
      }
    });
  }

  goBack(): void {
    window.history.back();
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  }

  formatPrice(amount: number): string {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount);
  }

  getOrderStatusClass(status: OrderStatus): string {
    switch (status) {
      case OrderStatus.Confirmed:
        return 'status-confirmed';
      case OrderStatus.Pending:
        return 'status-pending';
      case OrderStatus.Accepted:
        return 'status-accepted';
      case OrderStatus.Returned:
        return 'status-returned';
      default:
        return 'status-default';
    }
  }

  getOrderStatusText(status: OrderStatus): string {
    switch (status) {
      case OrderStatus.Confirmed:
        return 'Confirmed';
      case OrderStatus.Pending:
        return 'Pending';
      case OrderStatus.Accepted:
        return 'Accepted';
      case OrderStatus.Returned:
        return 'Returned';
      default:
        return 'Unknown';
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.loadOrders(page);
    }
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.goToPage(this.currentPage() - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.goToPage(this.currentPage() + 1);
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const total = this.totalPages();
    const current = this.currentPage();
    
    // Show max 5 page numbers at a time
    let start = Math.max(1, current - 2);
    let end = Math.min(total, start + 4);
    
    // Adjust start if we're near the end
    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  }
}
